<template>
  <footer :class="computedClasses">
    <TContainer :size="size">
      <div :class="['grid gap-x-8 gap-y-10 xl:gap-x-12', gridClasses]">
        <div class="[grid-area:localization] content-center  xl:justify-self-end xl:flex xl:gap-x-12">
          <AppLocalization />

          <button
            v-if="isExpandable"
            type="button"
            class="hidden xl:flex items-center gap-x-2 text-sm/6 font-semibold cursor-pointer hover:underline"
            @click="toggleExpand"
          >
            {{ t('supportAndResources') }} <TIcon
              :icon="['far', 'chevron-down']"
              :class="['text-secondary', { 'rotate-180': !isOpen }]"
            />
          </button>
        </div>

        <div
          v-if="!isCompact && !isHostMode"
          class="[grid-area:download-app] md:justify-self-end xl:justify-self-start flex flex-col md:flex-row xl:flex-col md:items-center xl:items-start gap-x-2 gap-y-1"
        >
          <span class="font-medium">{{ t('getTheApp') }}</span>
          <AppMobileAppDownloadButtons size="md" />
        </div>

        <nav
          v-if="!isCompact"
          :class="['[grid-area:links] grid grid-cols-2 gap-6', {
            'md:grid-cols-4': !isHostMode,
            'grid-cols-3': isHostMode,
          }]"
        >
          <div
            v-for="category in links"
            :key="category.title"
          >
            <THeading
              :level="7"
              tag="h4"
            >
              {{ category.title }}
            </THeading>

            <ul class="mt-1 space-y-0.25 xl:space-y-1 text-sm/6">
              <li
                v-for="link in category.links"
                :key="link.to"
              >
                <NuxtLink
                  :to="link.to"
                  :target="link.target"
                  :external="isExternal(link.to)"
                  :rel="link.rel"
                  class="text-gray-500 hover:underline"
                  @click="link?.onClick?.()"
                >
                  {{ link.title }}
                </NuxtLink>
              </li>
            </ul>
          </div>
        </nav>

        <div
          v-if="!isCompact && !isHostMode"
          class="[grid-area:social] flex gap-6 items-center"
        >
          <NuxtLink
            v-for="social in socialLinks"
            :key="social.url"
            :to="social.url"
            external
            :title="social.title"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center justify-center size-6"
          >
            <TIcon
              :icon="['fab', social.icon]"
              size="xl"
            />
          </NuxtLink>
        </div>

        <div
          v-if="!isCompact && !isHostMode"
          class="[grid-area:verified-reviews] flex items-center gap-4 flex-wrap md:justify-end xl:justify-start"
        >
          <VerifiedReviews />

          <TrustPilot />
        </div>

        <div class="[grid-area:copyright] text-gray-500 space-x-6 flex gap-y-4 text-sm/6 flex-col md:flex-row md:items-center">
          <div
            v-if="canShowCanadianFlag"
            class="flex items-center gap-x-1"
          >
            <TIcon
              :icon="['fab', 'canadian-maple-leaf']"
              class="text-canada-red"
              size="sm"
            />
            {{ t('proudlyCanadian') }}
          </div>

          <p>&copy; {{ t('copyrights', { currentYear }) }}</p>

          <div class="space-x-2">
            <NuxtLink
              :to="localePath('terms-service')"
              class="hover:underline"
            >
              {{ t('terms') }}
            </NuxtLink>

            <span>|</span>

            <NuxtLink
              :to="localePath('privacy-policy')"
              class="hover:underline"
            >
              {{ t('privacy') }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </TContainer>
  </footer>
</template>

<script setup lang="ts">
import type { IconName } from '@fortawesome/fontawesome-svg-core'
import type { ContainerSize } from '~/components/_tailwind/container/types'
import type { FooterVariant } from '~/components/app/types'
import { ROUTE_BASE_NAME } from '~/constants'

const props = withDefaults(defineProps<{
  variant?: FooterVariant
  size?: ContainerSize
  isSticky?: boolean
}>(), {
  variant: 'default',
})

const route = useRoute()
const { linkListRv } = useOwnerCommon()
const { userCountryString, userIsSuperhost, userExists } = useUser()
const { isInCa } = useGeolocation()
const localePath = useLocalePath()
const { locale, t } = useI18n()
const { isHostMode } = useBaseName()
const runtimeConfig = useRuntimeConfig()

const isDefault = computed(() => props.variant === 'default')
const isExpandable = computed(() => props.variant === 'expandable')
const isCompact = computed(() => props.variant === 'compact')

const computedClasses = computed(() => [
  'app-footer bg-gray-50 border-t border-gray-200',
  {
    'py-8': isDefault.value,
    'py-6 xl:py-4': !isDefault.value,
    'xl:fixed xl:bottom-0 xl:left-0 xl:right-0 xl:z-20 xl:transition-transform xl:duration-200 xl:ease-in-out': props.isSticky,
    'xl:translate-y-[calc(100%-58px)]': props.isSticky && !isOpen.value,
    'xl:translate-y-0': props.isSticky && isOpen.value,
  },
])

const isOpen = ref(false)
const canShowCanadianFlag = computed(() => route.meta?.layout !== 'static' && isInCa.value)

const currentYear = computed(() => {
  return datetime().year()
})

const { pageSource } = usePageSource()
function handleTrackHelpCenterClicked() {
  trackHelpCenterClicked({
    pageSource: pageSource.value,
  })
}

function handleTrackSuperhostClicked() {
  trackSuperhostLearnMoreClicked({
    accountCountry: userCountryString.value ? userCountryString.value.toLowerCase() : undefined,
    isSuperhost: userExists ? userIsSuperhost.value : undefined,
    pageSource: pageSource.value,
  })
}

function toggleExpand() {
  isOpen.value = !isOpen.value
}

function isExternal(url: string) {
  return url.includes('http')
}

const gridClasses = computed(() => {
  if (isHostMode.value) {
    // eslint-disable-next-line @stylistic/quotes
    return "[grid-template-areas:'localization''copyright''links'] xl:[grid-template-areas:'copyright_localization''links_links']"
  }

  return {
  // eslint-disable-next-line @stylistic/quotes
    "[grid-template-areas:'localization''download-app''links''social''verified-reviews''copyright'] md:[grid-template-areas:'localization_download-app''links_links''social_verified-reviews''copyright_copyright'] xl:[grid-template-areas:'copyright_copyright_localization''download-app_links_links''social_links_links''verified-reviews_links_links'] xl:grid-cols-[auto_1fr_auto]": !isCompact.value,
    // eslint-disable-next-line @stylistic/quotes
    "[grid-template-areas:'localization''copyright'] xl:[grid-template-areas:'copyright_localization']": isCompact.value,
  }
})

const links = computed<{ title: string, links: { title: string, to: string, target?: string, rel?: string, onClick?: () => void }[] }[]>(() => {
  if (isHostMode.value) {
    return [
      {
        title: t('support'),
        links: [
          {
            title: t('pageLink.helpCenter'),
            to: t('helpCenter.url'),
            target: '_blank',
          },
          {
            title: t('howItWorks'),
            to: localePath({ name: 'how-it-works', hash: locale.value === 'fr' ? '#comment-inscrire-votre-vr' : '#how-to-list-your-rv' }),
          },
          {
            title: t('serviceFees'),
            to: localePath('service-fees'),
          },
          {
            title: t('cancellationPolicy'),
            to: t('helpUrls.cancellationPolicyHost'),
            target: '_blank',
          },
          {
            title: t('contactUs.title'),
            to: t('helpUrls.newRequest'),
            target: '_blank',
          },
        ],
      },

      {
        title: t('insuranceInformation'),
        links: [
          {
            title: t('hostOverview'),
            to: localePath('insurance-and-protection'),
          },
          {
            title: t('whatIsCoveredNotCovered.title'),
            to: t('whatIsCoveredNotCovered.url'),
            target: '_blank',
          },
          {
            title: t('borderCrossingCoverage.title'),
            to: t('borderCrossingCoverage.url'),
            target: '_blank',
          },
          {
            title: t('whatIsTheInsuranceApplication.title'),
            to: t('whatIsTheInsuranceApplication.url'),
            target: '_blank',
          },
          {
            title: t('restrictedLocations.title'),
            to: t('restrictedLocations.url'),
            target: '_blank',
          },
        ],
      },
      {
        title: t('hosting'),
        links: [
          {
            title: t('listingYourRV'),
            to: localePath('owner'),
          },
          {
            title: t('learnAboutNewFeatures'),
            to: localePath({ name: 'category-slug', params: { slug: 'product-blog' } }),
          },
          {
            title: t('getting5StarReview'),
            to: localePath({ name: 'blog-slug', params: { slug: 'how-to-get-5-star-reviews-everytime' } }),
          },
          {
            title: t('gettingPaidAsAHost.title'),
            to: t('gettingPaidAsAHost.url'),
            target: '_blank',
          },
        ],
      },
    ]
  }

  return [
    {
      title: t('company'),
      links: [
        {
          title: t('about'),
          to: localePath('about'),
        },
        {
          title: t('blog'),
          to: localePath('blog'),
        },
        ...(locale.value === 'en'
          ? [{
              title: t('careers'),
              to: localePath('careers', 'en'),
            }]
          : []),
        {
          title: t('pressAndMedia'),
          to: localePath('press-and-media'),
        },
        {
          title: t('brandGuidelines'),
          to: 'https://company-209628.frontify.com/d/qwb7VrkogegN',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
        {
          title: t('affiliates'),
          to: localePath('affiliates'),
        },
        {
          title: t('reviews'),
          to: localePath('reviews'),
        },
        {
          title: t('rvezyStore'),
          to: 'https://rvezy.store/',
          target: '_blank',
        },
      ],
    },
    {
      title: t('getStarted'),
      links: [
        {
          title: t('searchRVs'),
          to: localePath('rv-search'),
        },
        {
          title: t('listYourRV'),
          to: linkListRv.value,
        },
        {
          title: t('rvRentals'),
          to: localePath('rv-rentals'),
        },
        {
          title: t('buyAnRV'),
          to: `${runtimeConfig.public.apps.marketplace.baseURL}/${locale.value}`,
        },
        {
          title: t('searchCampgrounds'),
          to: localePath(ROUTE_BASE_NAME.campgrounds),
        },
        {
          title: t('downloadRVezyApp'),
          to: localePath('app-download'),
        },
        {
          title: t('superhost'),
          to: localePath('superhost'),
          onClick: handleTrackSuperhostClicked,
        },
        {
          title: t('events'),
          to: localePath(ROUTE_BASE_NAME.events),

        },
        {
          title: t('parks'),
          to: localePath('parks'),
        },
      ],
    },
    {
      title: t('helpfulLinks'),
      links: [
        {
          title: t('howItWorks'),
          to: localePath('how-it-works'),
        },
        {
          title: t('rvDelivery'),
          to: localePath('rv-delivery'),
        },
        {
          title: t('insuranceAndProtection'),
          to: localePath('insurance-and-protection'),
        },
        {
          title: t('trustAndSafety'),
          to: localePath('trust-and-safety'),
        },
        {
          title: t('serviceFees'),
          to: localePath('service-fees'),
        },
        {
          title: t('cancellationPolicy'),
          to: t('helpUrls.cancellationPolicyGuest'),
          onClick: handleTrackHelpCenterClicked,
        },
        {
          title: t('shareYourTrip'),
          to: localePath('share-your-trip'),
        },
      ],
    },
    {
      title: t('support'),
      links: [
        {
          title: t('helpCenter.title'),
          to: t('helpCenter.url'),
          onClick: handleTrackHelpCenterClicked,
        },
        {
          title: t('contactUs.title'),
          to: t('contactUs.url'),
          onClick: handleTrackHelpCenterClicked,
        },
        {
          title: 'LLM Information',
          to: localePath('llm-information', 'en'),
        },
      ],
    },
  ]
})

const socialLinks = computed<{ url: string, title: string, icon: IconName }[]>(() => [
  {
    url: 'https://www.facebook.com/rvezy',
    title: 'RVezy on Facebook',
    icon: 'facebook',
  },
  {
    url: 'https://www.youtube.com/c/RVezy',
    title: 'RVezy on Youtube',
    icon: 'youtube',
  },
  {
    url: 'https://www.instagram.com/myrvezy',
    title: 'RVezy on Instagram',
    icon: 'instagram',
  },
  {
    url: 'https://x.com/myrvezy',
    title: 'RVezy on X',
    icon: 'x-twitter',
  },
  {
    url: 'https://ca.linkedin.com/company/rvezy.com',
    title: 'RVezy on Linkedin',
    icon: 'linkedin-in',
  },
])
</script>

<i18n src="~~/i18n/locales/common/mobile-app.json" lang="json" />

<i18n src="~~/i18n/locales/common/app.json" lang="json" />

<i18n lang="json">
{
  "en": {
    "about": "About",
    "affiliates": "Affiliates",
    "blog": "Blog",
    "brandGuidelines": "Brand guidelines",
    "buyAnRV": "Buy an RV",
    "cancellationPolicy": "Cancellation policy",
    "careers": "Careers",
    "pressAndMedia": "Press and media",
    "company": "Company",
    "proudlyCanadian": "Proudly Canadian",
    "copyrights": "{currentYear} RVezy. All rights reserved.",
    "downloadRVezyApp": "Download the RVezy App",
    "superhost": "Superhost",
    "getStarted": "Get started",
    "helpfulLinks": "Helpful links",
    "howItWorks": "How it works",
    "events": "Events",
    "insuranceAndProtection": "Insurance and protection",
    "listYourRV": "List your RV",
    "privacy": "Privacy",
    "reviews": "Reviews",
    "rvezyStore": "RVezy Store",
    "rvDelivery": "RV delivery",
    "rvRentals": "RV Rentals",
    "searchCampgrounds": "Search campgrounds",
    "searchRVs": "Search RVs",
    "serviceFees": "Service fees",
    "support": "Support",
    "supportAndResources": "Support and resources",
    "terms": "Terms",
    "trustAndSafety": "Trust and safety",
    "shareYourTrip": "Share your trip",
    "parks": "Parks",

    "insuranceInformation": "Insurance Information",
    "hostOverview": "Overview for hosts",
    "whatIsCoveredNotCovered": {
      "title": "What is covered/not covered?",
      "url": "https://help.rvezy.com/hc/en-us/articles/34006572813851"
    },
    "borderCrossingCoverage": {
      "title": "Border crossing coverage",
      "url": "https://help.rvezy.com/hc/en-us/articles/43657360096027"
    },
    "whatIsTheInsuranceApplication": {
      "title": "What is the Insurance Application?",
      "url": "https://help.rvezy.com/hc/en-us/articles/360039295194"
    },
    "restrictedLocations": {
      "title": "Restricted locations",
      "url": "https://help.rvezy.com/hc/en-us/articles/360039340074"
    },

    "hosting": "Hosting",
    "listingYourRV": "Listing your RV",
    "learnAboutNewFeatures": "Learn about new features",
    "getting5StarReview": "Getting a 5-star review",
    "gettingPaidAsAHost": {
      "title": "Getting paid as a host",
      "url": "https://help.rvezy.com/hc/en-us/articles/360038105934"
    }
  },
  "fr": {
    "about": "À propos",
    "affiliates": "Programme d’affiliation",
    "blog": "Blogue",
    "brandGuidelines": "Directives de marque",
    "pressAndMedia": "Presse et médias",
    "buyAnRV": "Acheter un VR",
    "cancellationPolicy": "Politique d’annulation",
    "company": "Entreprise",
    "proudlyCanadian": "Fièrement canadien",
    "copyrights": "{currentYear} RVezy. Tous droits réservés.",
    "downloadRVezyApp": "Télécharger l’App Rvezy",
    "superhost": "Superhôtes",
    "getStarted": "Pour démarrer",
    "helpfulLinks": "Liens utiles",
    "howItWorks": "Comment ça marche",
    "events": "Événements",
    "insuranceAndProtection": "Assurances et protection",
    "listYourRV": "Inscrire un VR",
    "privacy": "Confidentialité",
    "reviews": "Évaluations",
    "rvezyStore": "Magasin RVezy",
    "rvDelivery": "Livraison de VR",
    "rvRentals": "Locations de VR",
    "searchCampgrounds": "Rechercher un camping",
    "searchRVs": "Rechercher un VR",
    "serviceFees": "Frais de service",
    "support": "Support",
    "supportAndResources": "Support et ressources",
    "terms": "Termes",
    "trustAndSafety": "Confiance et sécurité",
    "shareYourTrip": "Partagez votre voyage",
    "parks": "Parcs",

    "insuranceInformation": "Informations sur l’assurance",
    "hostOverview":  "Aperçu pour les hôtes",

    "whatIsCoveredNotCovered": {
      "title": "Ce qui est couvert/non couvert?",
      "url": "https://help.rvezy.com/hc/fr-ca/articles/34006572813851"
    },
    "borderCrossingCoverage": {
      "title": "Couverture hors frontière",
      "url": "https://help.rvezy.com/hc/fr-ca/articles/43657360096027"
    },
    "whatIsTheInsuranceApplication": {
      "title": "Qu’est-ce qu’est la demande d’assurance?",
      "url": "https://help.rvezy.com/hc/fr-ca/articles/360039295194"
    },
    "restrictedLocations": {
      "title": "Zones restreintes",
      "url": "https://help.rvezy.com/hc/fr-ca/articles/360039340074"
    },

    "hosting": "Section hôtes",
    "listingYourRV": "Afficher votre VR",
    "learnAboutNewFeatures": "Découvrir les nouvelles fonctionnalités",
    "getting5StarReview": "Obtenir un avis de 5 étoiles",
    "gettingPaidAsAHost": {
      "title": "Être payé en tant qu’hôte",
      "url": "https://help.rvezy.com/hc/fr-ca/articles/360038105934"
    }
  }
}
</i18n>
